import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {CredentialResponse, GoogleLogin} from '@react-oauth/google'
import styled from 'styled-components'
import {useForm} from 'react-hook-form'
import {AUTH_LOGIN_MAX_ATTEMPT} from 'consts'
import {useTranslation} from 'i18n'
import {AuthRegisterWaitingModal, GiftShopTemplateModalMFA} from 'pages'
import {requestData} from 'services'
import {showBanModal, showSnackbar, useHistory} from 'utils'
import {
  AuthFormSimpleRegisterData,
  AuthLoginResponse,
  AuthLoginUnverifiedResponse,
} from 'types'
import {Button, IconGoogle, Paragraph} from 'common/components'
import convertUnit from 'lib/unit'
import {useDispatch, useSelector} from 'lib/redux'
import {TemplateAuthAccessUsernameModal} from '../../../../template'
import {AuthLoginGoogleProps} from './AuthLoginGoogleProps'

const StyledContainer = styled.div`
  display: flex;
  flex: 1;
  position: relative;
`

const StyledButtonLoginGoogle = styled(Button)`
  ${({theme}) => ({borderColor: theme.white_3})}
  border-style: solid;
  border-width: ${convertUnit(1)};
  display: flex;
  flex: 1;
  position: absolute;
  z-index: 1;
  pointer-events: none;
  width: 100%;
`

const StyledIconGoogle = styled(IconGoogle)`
  margin-right: ${convertUnit(10)};
`

const StyledGoogleLoginContainer = styled.div`
  display: flex;
  flex: 1;
  position: relative;
  border-radius: ${convertUnit(22)};
  top: ${convertUnit(0)};
`

const StyledGoogleLogin = styled(GoogleLogin)`
  width: 100%;
  border-radius: 50%;
`

const StyledLabel = styled(Paragraph)`
  text-align: center;
`

export default function AuthLoginGoogle({
  label,
  buttonStyle,
  shouldRedirect = true,
  onClose,
}: AuthLoginGoogleProps) {
  const {translate} = useTranslation()
  const [loading, setLoading] = useState(false)
  const history = useHistory()
  const [waitingModal, setWaitingModal] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState<number>()
  const {tag_id, tree_id} = useSelector('treeQrState') || {}
  const {update} = useDispatch()
  const form = useForm<AuthFormSimpleRegisterData>({
    defaultValues: {
      username: '',
    },
  })
  const {setError} = form
  const stateModalUsername = useState(false)
  const stateModalUsernameLoading = useState(false)
  const [ssoToken, setSsoToken] = useState('')
  const stateModalMFA = useState(false)
  const setModalMFA = stateModalMFA[1]
  const stateMFALoading = useState(false)
  const setMFALoading = stateMFALoading[1]
  const stateMFAError = useState('')
  const setMFAError = stateMFAError[1]

  const handleRenderWaitingModal = useMemo(
    () => (
      <AuthRegisterWaitingModal
        visible={waitingModal}
        toggleModal={() => setWaitingModal(false)}
      />
    ),
    [waitingModal],
  )

  const handleEasySignupLogin = useCallback(
    (tokenId: string, username?: string, totp?: string) => {
      if (username) {
        stateModalUsernameLoading[1](true)
      } else if (totp) {
        setMFALoading(true)
      }
      requestData('auth_easy_signup_login_sso_google', {
        data: {token: tokenId, username, mfa_code: totp},
        onRequestReceived: () => {
          stateModalUsernameLoading[1](false)
          setMFALoading(false)
        },
        onRequestSuccess: ({data, status}) => {
          if (status === 200) {
            setModalMFA(false)
            setMFAError('')
            const userInfo = data.result as AuthLoginResponse
            update('user', userInfo)
            update('lastUserState', {
              username: undefined,
              photo: undefined,
              refreshToken: undefined,
            })
            if (tree_id && tag_id) {
              history.pushQuery({
                path: 'tree_events_bib_masonry',
                state: {
                  tree_url: '',
                  tree_name: '',
                  fromScan: true,
                },
                queryObject: {
                  tree_id,
                  tag_id,
                  search: '',
                },
              })
              return
            }
            if (shouldRedirect) {
              const {search} = history.location
              if (search.startsWith('?next')) {
                if (history.length > 1) {
                  history.goBack()
                } else {
                  window.location.replace(decodeURIComponent(search))
                }
                return
              }
              location.reload()
            } else {
              onClose && onClose()
            }
          } else if (status === 202) {
            const result = data.result as AuthLoginUnverifiedResponse
            if (result.child_consent_code) {
              history.push('auth_easy_signup_underage', {
                child_consent_code: data.result.child_consent_code,
                token: tokenId,
                method: 'gosso',
              })
            } else if (result.recovery_code) {
              const {recovery_code, remaining_time, member_id} = result
              history.push('auth_recover_account', {
                recovery_code,
                remaining_time,
                member_id,
                token: tokenId,
                method: 'gosso',
              })
            } else {
              history.push('auth_easy_signup_sso', {
                token: tokenId,
                ssoMethod: 'gosso',
                suggestions: result.suggestions,
              })
            }
          } else if (status === 400 && data.detail) {
            if (data.detail.username === 'invalid') {
              setError('username', {
                type: 'unavailable',
                message: translate('auth:registerEasySignupUsernameNotMatch'),
              })
            } else if (data.detail.mfa_attempt) {
              setMFAError(
                translate('giftShop:settingAuthAppInvalidError', {
                  count:
                    AUTH_LOGIN_MAX_ATTEMPT -
                    parseInt(data.detail.mfa_attempt, 10),
                }),
              )
            } else if (data.detail.mfa_code === 'required') {
              setModalMFA(true)
            }
          } else if (status === 401) {
            stateModalUsername[1](true)
          } else if (
            status === 423 &&
            data.message === 'feature suspended' &&
            data.detail
          ) {
            setModalMFA(false)
            setMFAError('')
            showBanModal(status, 'Attempt Login Suspended')
          } else if (status === 503) {
            setModalMFA(false)
            setMFAError('')
            setWaitingModal(true)
          } else {
            setModalMFA(false)
            setMFAError('')
          }
        },
        onRequestFailed: () => {
          showSnackbar(translate('giftShop:snackbarFailedExecute'))
          setLoading(false)
        },
      })
    },
    [
      history,
      onClose,
      setError,
      setMFAError,
      setMFALoading,
      setModalMFA,
      shouldRedirect,
      stateModalUsername,
      stateModalUsernameLoading,
      tag_id,
      translate,
      tree_id,
      update,
    ],
  )

  const handleSuccess = useCallback(
    (response: CredentialResponse) => {
      const {credential: tokenId} = response
      if (tokenId) {
        setSsoToken(tokenId)
        handleEasySignupLogin(tokenId)
      }
    },
    [handleEasySignupLogin],
  )

  const handleGoogleLogin = useMemo(
    () => (
      <StyledGoogleLogin
        cancel_on_tap_outside
        onSuccess={handleSuccess}
        shape="pill"
        width={`${width}px`}
      />
    ),
    [handleSuccess, width],
  )

  const handleRenderModalUsername = useMemo(
    () => (
      <TemplateAuthAccessUsernameModal
        stateVisible={stateModalUsername}
        stateLoading={stateModalUsernameLoading}
        form={form}
        onClick={(username) => handleEasySignupLogin(ssoToken, username)}
      />
    ),
    [
      form,
      handleEasySignupLogin,
      ssoToken,
      stateModalUsername,
      stateModalUsernameLoading,
    ],
  )

  const handleRenderModalMFA = useMemo(
    () => (
      <GiftShopTemplateModalMFA
        onConfirm={(code) => handleEasySignupLogin(ssoToken, undefined, code)}
        stateError={stateMFAError}
        stateLoading={stateMFALoading}
        stateModal={stateModalMFA}
      />
    ),
    [
      handleEasySignupLogin,
      ssoToken,
      stateMFAError,
      stateMFALoading,
      stateModalMFA,
    ],
  )

  useLayoutEffect(() => {
    setWidth(ref.current?.offsetWidth)
  }, [])

  return (
    <StyledContainer ref={ref}>
      {handleRenderWaitingModal}
      {handleRenderModalUsername}
      {handleRenderModalMFA}
      <StyledButtonLoginGoogle
        leftIcon={<StyledIconGoogle />}
        backgroundColor="white_3"
        color="primary_5"
        isLoading={loading}
        disabled={loading}
        style={buttonStyle}>
        <StyledLabel color="primary_5" fontSize="m" fontWeight="bold">
          {label}
        </StyledLabel>
      </StyledButtonLoginGoogle>
      <StyledGoogleLoginContainer>
        {handleGoogleLogin}
      </StyledGoogleLoginContainer>
    </StyledContainer>
  )
}
